// ==========================================================================
// NATIIVE DIGITAL - MAIN JAVASCRIPT
// ==========================================================================

// Universal Mouse Tracker Class (DRY principle)
class MouseTracker {
    constructor(selector, options = {}) {
        this.selector = selector;
        this.algorithm = options.algorithm || 'smooth';
        this.useMouseEnter = options.useMouseEnter || false;
        this.range = options.range || 60;
        this.overlaySelector = options.overlaySelector || null;
        this.init();
    }

    init() {
        const elements = document.querySelectorAll(this.selector);
        elements.forEach(element => this.bindElement(element));
    }

    bindElement(container) {
        // Skip page-hero sections — grid is static on these page headers
        if (container.classList.contains('page-hero')) return;

        // Check for data attributes that might override options
        const algorithm = container.dataset.gridAlgorithm || this.algorithm;
        const useMouseEnter = container.dataset.gridEnter === 'true' || this.useMouseEnter;

        // Find the overlay element - either from options or as child
        const overlay = this.overlaySelector
            ? container.querySelector(this.overlaySelector)
            : container.querySelector('.grid-overlay, .service-grid-overlay, .work-grid-overlay');

        if (!overlay) return;

        let mouseActive = false;

        // Optional mouse enter handling
        if (useMouseEnter) {
            container.addEventListener('mouseenter', () => {
                if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
                mouseActive = true;
                overlay.classList.add('mouse-active');
            });
        }

        container.addEventListener('mousemove', (e) => {
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

            // For containers with mouseenter, check if active
            if (useMouseEnter && !mouseActive) return;

            const rect = container.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            // Calculate movement based on algorithm
            const moveX = (x / rect.width) * this.range - (this.range / 2);
            const moveY = (y / rect.height) * this.range - (this.range / 2);

            overlay.style.transform = `translate(${moveX}px, ${moveY}px)`;

            // Add mouse-active class for containers that don't use mouseenter
            if (!useMouseEnter) {
                overlay.classList.add('mouse-active');
            }
        });

        container.addEventListener('mouseleave', () => {
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

            if (useMouseEnter) {
                mouseActive = false;
            }
            overlay.classList.remove('mouse-active');
            // Grid stays in its last position - no reset
        });
    }
}

// Theme Toggle Functionality
function initThemeToggle() {
    const themeToggle = document.getElementById('themeToggle');
    const body = document.body;

    if (!themeToggle) return;

    // SVG icons for sun and moon
    const sunIcon = `<svg class="theme-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="5" stroke="currentColor" stroke-width="2"/>
        <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    </svg>`;

    const moonIcon = `<svg class="theme-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

    // Check for saved theme preference or default to 'dark'
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light') {
        body.classList.add('light-theme');
        themeToggle.innerHTML = moonIcon;
        themeToggle.setAttribute('aria-label', 'Switch to dark mode');
    } else {
        themeToggle.innerHTML = sunIcon;
        themeToggle.setAttribute('aria-label', 'Switch to light mode');
    }

    themeToggle.addEventListener('click', () => {
        body.classList.toggle('light-theme');

        if (body.classList.contains('light-theme')) {
            themeToggle.innerHTML = moonIcon;
            themeToggle.setAttribute('aria-label', 'Switch to dark mode');
            localStorage.setItem('theme', 'light');
        } else {
            themeToggle.innerHTML = sunIcon;
            themeToggle.setAttribute('aria-label', 'Switch to light mode');
            localStorage.setItem('theme', 'dark');
        }
    });
}

// Mobile Menu Toggle Functionality
function initMobileMenu() {
    const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
    const mainNav = document.querySelector('.main-nav');
    const body = document.body;

    if (!mobileMenuToggle || !mainNav) return;

    // Toggle mobile menu open/close
    mobileMenuToggle.addEventListener('click', () => {
        const isExpanded = mobileMenuToggle.getAttribute('aria-expanded') === 'true';

        // Toggle active classes
        mobileMenuToggle.classList.toggle('active');
        mainNav.classList.toggle('active');

        // Update aria-expanded for accessibility
        mobileMenuToggle.setAttribute('aria-expanded', !isExpanded);

        // Prevent body scroll when menu is open
        if (!isExpanded) {
            body.style.overflow = 'hidden';
        } else {
            body.style.overflow = '';
        }
    });

    // Handle mobile dropdown menu clicks
    const dropdowns = mainNav.querySelectorAll('.dropdown');
    dropdowns.forEach(dropdown => {
        const dropdownLink = dropdown.querySelector('a');

        dropdownLink.addEventListener('click', (e) => {
            // Only prevent default on mobile when clicking dropdown toggle
            if (window.innerWidth <= 900) {
                e.preventDefault();
                dropdown.classList.toggle('active');

                // Find dropdown menu and toggle it
                const dropdownMenu = dropdown.querySelector('.dropdown-menu');
                if (dropdownMenu) {
                    dropdownMenu.classList.toggle('active');
                }
            }
        });
    });

    // Handle mobile nested submenu toggle buttons (.has-submenu > .submenu-toggle)
    // The parent <a> remains clickable so users can navigate to the service overview page;
    // the dedicated chevron button expands/collapses the sub-list.
    const subMenuToggles = mainNav.querySelectorAll('.has-submenu > .submenu-toggle');
    subMenuToggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
            if (window.innerWidth <= 900) {
                e.preventDefault();
                e.stopPropagation();
                const item = toggle.closest('.has-submenu');
                const isActive = item.classList.toggle('active');
                const submenu = item.querySelector(':scope > .submenu');
                if (submenu) submenu.classList.toggle('active', isActive);
                toggle.setAttribute('aria-expanded', String(isActive));
            }
        });
    });

    // Close mobile menu when clicking outside
    document.addEventListener('click', (e) => {
        if (window.innerWidth <= 900 &&
            mainNav.classList.contains('active') &&
            !mainNav.contains(e.target) &&
            !mobileMenuToggle.contains(e.target)) {

            mobileMenuToggle.classList.remove('active');
            mainNav.classList.remove('active');
            mobileMenuToggle.setAttribute('aria-expanded', 'false');
            body.style.overflow = '';
        }
    });

    // Close mobile menu when clicking navigation links (except the Services dropdown toggle,
    // which only opens its submenu rather than navigating).
    const navLinks = mainNav.querySelectorAll('a');
    navLinks.forEach(link => {
        link.addEventListener('click', (e) => {
            const isDropdownToggle = link.closest('.dropdown') &&
                                     link === link.closest('.dropdown').querySelector('a');

            if (!isDropdownToggle && window.innerWidth <= 900) {
                mobileMenuToggle.classList.remove('active');
                mainNav.classList.remove('active');
                mobileMenuToggle.setAttribute('aria-expanded', 'false');
                body.style.overflow = '';
            }
        });
    });

    // Close mobile menu on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && mainNav.classList.contains('active')) {
            mobileMenuToggle.classList.remove('active');
            mainNav.classList.remove('active');
            mobileMenuToggle.setAttribute('aria-expanded', 'false');
            body.style.overflow = '';
        }
    });

    // Reset menu state when resizing to desktop
    window.addEventListener('resize', () => {
        if (window.innerWidth > 900) {
            mobileMenuToggle.classList.remove('active');
            mainNav.classList.remove('active');
            mobileMenuToggle.setAttribute('aria-expanded', 'false');
            body.style.overflow = '';

            // Remove active class from all dropdowns
            dropdowns.forEach(dropdown => {
                dropdown.classList.remove('active');
                const dropdownMenu = dropdown.querySelector('.dropdown-menu');
                if (dropdownMenu) {
                    dropdownMenu.classList.remove('active');
                }
            });
        }
    });
}

// Simplified Mouse Tracking Initialization (DRY principle)
function initMouseTracking() {
    // Generic grid overlays (with data attribute configuration)
    new MouseTracker('[data-grid-algorithm], [data-grid-enter]', {
        algorithm: 'smooth',
        useMouseEnter: false
    });

    // Service cards (require mouse enter)
    new MouseTracker('.service-card', {
        algorithm: 'smooth',
        useMouseEnter: true,
        overlaySelector: '.service-grid-overlay'
    });

    // Work cards (require mouse enter)
    new MouseTracker('.work-card', {
        algorithm: 'smooth',
        useMouseEnter: true,
        overlaySelector: '.work-grid-overlay'
    });

    // FAQ accordions (require mouse enter)
    new MouseTracker('.accordion', {
        algorithm: 'smooth',
        useMouseEnter: true,
        overlaySelector: '.faq-grid-overlay'
    });

    // Service sub-page cards inside dark-secondary sections — inject overlay and track
    const altSectionCards = document.querySelectorAll(
        '.service-section--alt .offering-card, .service-section--alt .feature-card, .service-section--alt .process-step'
    );
    altSectionCards.forEach(card => {
        if (!card.querySelector('.service-grid-overlay')) {
            const overlay = document.createElement('div');
            overlay.className = 'service-grid-overlay';
            overlay.setAttribute('aria-hidden', 'true');
            card.prepend(overlay);
        }
    });

    if (altSectionCards.length) {
        new MouseTracker(
            '.service-section--alt .offering-card, .service-section--alt .feature-card, .service-section--alt .process-step',
            { algorithm: 'smooth', useMouseEnter: true, overlaySelector: '.service-grid-overlay' }
        );
    }
}

// Smart Header scroll-direction functionality
function initSmartHeader() {
    const header = document.querySelector('header');
    if (!header) return;

    let lastScroll = window.scrollY;
    let ticking = false;

    window.addEventListener('scroll', () => {
        if (!ticking) {
            window.requestAnimationFrame(() => {
                const currentScroll = window.scrollY;
                
                // Add scrolled background when off the very top
                if (currentScroll > 50) {
                    header.classList.add('header-scrolled');
                } else {
                    header.classList.remove('header-scrolled');
                }

                // Hide on scroll down, show on scroll up (with a threshold so it doesn't hide at the very top)
                // Never hide while inside the polaroid-hero section (flag set by polaroid-hero.js)
                if (currentScroll > lastScroll && currentScroll > 200 && !window._inPolaroidHero) {
                    // Only hide if the mobile menu is not active
                    const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
                    if (!mobileMenuToggle || !mobileMenuToggle.classList.contains('active')) {
                        header.classList.add('header-hidden');
                    }
                } else {
                    header.classList.remove('header-hidden');
                }
                
                lastScroll = currentScroll <= 0 ? 0 : currentScroll;
                ticking = false;
            });
            ticking = true;
        }
    }, { passive: true });
}

// Contact Form Handling
function initContactForm() {
    const contactForm = document.querySelector('.contact-form');
    if (contactForm) {
        const formLoadedAt = Date.now();
        // Blank data-verify-api = same-origin Pages Functions (/api/...). "off"
        // keeps the no-send simulated submit. Any other value = a base URL.
        const rawApi = (contactForm.dataset.verifyApi || '').trim();
        const verifyDisabled = rawApi === 'off';
        const apiBase = verifyDisabled ? '' : rawApi.replace(/\/$/, '');
        // Email-verification toggle. When false, the form submits the enquiry
        // directly (anti-spam still applies) without the emailed 6-digit code.
        // Flip to true to re-enable the code flow (e.g. for support tickets) —
        // the verification machinery below is kept intact for that reuse.
        // Must match REQUIRE_EMAIL_VERIFICATION in functions/api/submit-enquiry.js.
        const EMAIL_VERIFICATION_ENABLED = false;
        const modal = document.getElementById('verifyModal');
        const codeInput = document.getElementById('verifyCode');
        const errorEl = document.getElementById('verifyError');
        const messageEl = document.getElementById('verifyMessage');
        const submitBtn = document.getElementById('verifySubmit');
        const resendBtn = document.getElementById('verifyResend');

        let verifyToken = null;
        let pendingPayload = null;

        const setError = (msg) => {
            if (!errorEl) return;
            if (!msg) { errorEl.hidden = true; errorEl.textContent = ''; return; }
            errorEl.hidden = false;
            errorEl.textContent = msg;
        };

        const openModal = () => {
            if (!modal) return;
            modal.hidden = false;
            document.body.style.overflow = 'hidden';
            setTimeout(() => codeInput && codeInput.focus(), 50);
        };
        const closeModal = () => {
            if (!modal) return;
            modal.hidden = true;
            document.body.style.overflow = '';
            setError('');
            if (codeInput) codeInput.value = '';
        };

        if (modal) {
            modal.querySelectorAll('[data-verify-close]').forEach(el => el.addEventListener('click', closeModal));
            modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });
            document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !modal.hidden) closeModal(); });
        }

        const collectFormData = () => {
            const fd = new FormData(contactForm);
            const obj = {};
            fd.forEach((v, k) => { obj[k] = v; });
            return obj;
        };

        // Cloudflare Turnstile token (hidden field the widget injects) + a reset
        // so a fresh, single-use token is ready after every submit attempt.
        const getTurnstileToken = () =>
            (pendingPayload && pendingPayload['cf-turnstile-response']) ||
            (window.turnstile && window.turnstile.getResponse && window.turnstile.getResponse()) || '';
        const resetTurnstile = () => {
            try { if (window.turnstile) window.turnstile.reset(); } catch (_) { /* widget not ready */ }
        };

        const requestCode = async (email) => {
            const res = await fetch(`${apiBase}/api/send-code`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({
                    email,
                    ts: formLoadedAt,
                    hp: (pendingPayload && pendingPayload.company_url) || '',
                    turnstileToken: getTurnstileToken(),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const msg = data.detail ? `${data.error || 'send_failed'}: ${data.detail}` : (data.error || 'send_failed');
                console.error('[contact] send-code failed —', msg);
                throw new Error(msg);
            }
            return data.token;
        };

        const submitVerifiedEnquiry = async (code) => {
            const res = await fetch(`${apiBase}/api/submit-enquiry`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ ...pendingPayload, token: verifyToken, code }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const msg = data.detail ? `${data.error || 'submit_failed'}: ${data.detail}` : (data.error || 'submit_failed');
                console.error('[contact] submit-enquiry failed —', msg);
                throw new Error(msg);
            }
            return data;
        };

        // Direct submit used when EMAIL_VERIFICATION_ENABLED is false: posts the
        // enquiry without a token/code. `ts` lets the server apply its timing
        // anti-spam gate (the one that normally lives in send-code.js).
        const submitEnquiryDirect = async () => {
            const res = await fetch(`${apiBase}/api/submit-enquiry`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ ...pendingPayload, ts: formLoadedAt, turnstileToken: getTurnstileToken() }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const msg = data.detail ? `${data.error || 'submit_failed'}: ${data.detail}` : (data.error || 'submit_failed');
                console.error('[contact] submit-enquiry (direct) failed —', msg);
                throw new Error(msg);
            }
            return data;
        };

        contactForm.addEventListener('submit', async (e) => {
            e.preventDefault();

            // Native HTML5 validity check (we set novalidate on the form)
            if (!contactForm.checkValidity()) {
                contactForm.reportValidity();
                return;
            }

            pendingPayload = collectFormData();

            // Explicitly disabled (data-verify-api="off") — simulated alert.
            if (verifyDisabled) {
                alert("Thank you for your message! We'll get back to you within 24 hours.");
                contactForm.reset();
                return;
            }

            const sendBtn = contactForm.querySelector('button[type="submit"]');
            const originalText = sendBtn ? sendBtn.textContent : '';

            // Email verification turned off — submit the enquiry directly,
            // skipping the 6-digit-code modal entirely.
            if (!EMAIL_VERIFICATION_ENABLED) {
                if (sendBtn) { sendBtn.disabled = true; sendBtn.textContent = 'Sending…'; }
                try {
                    await submitEnquiryDirect();
                    contactForm.reset();
                    alert("Thanks — your enquiry has been sent. We'll get back to you within 24 hours.");
                } catch (err) {
                    const msg = err.message === 'turnstile_failed'
                        ? "We couldn't confirm you're human. Please complete the checkbox and try again."
                        : `Couldn't send your enquiry: ${err.message}. Please try again or call us on 1300 78 33 11.`;
                    alert(msg);
                } finally {
                    if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = originalText; }
                    resetTurnstile(); // refresh the single-use token for any retry
                }
                return;
            }

            if (sendBtn) { sendBtn.disabled = true; sendBtn.textContent = 'Sending code…'; }

            try {
                verifyToken = await requestCode(pendingPayload.email);
                if (messageEl) messageEl.textContent = `We've sent a 6-digit code to ${pendingPayload.email}. Enter it below to confirm and submit your enquiry.`;
                openModal();
            } catch (err) {
                alert(`Couldn't send verification code: ${err.message}. Please try again or call us on 1300 78 33 11.`);
            } finally {
                if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = originalText; }
            }
        });

        if (submitBtn) {
            submitBtn.addEventListener('click', async () => {
                setError('');
                const code = (codeInput && codeInput.value || '').trim();
                if (!/^[0-9]{6}$/.test(code)) {
                    setError('Please enter the 6-digit code from your email.');
                    return;
                }
                submitBtn.disabled = true;
                submitBtn.textContent = 'Verifying…';
                try {
                    await submitVerifiedEnquiry(code);
                    closeModal();
                    contactForm.reset();
                    alert("Thanks — your enquiry has been verified and sent. We'll get back to you within 24 hours.");
                } catch (err) {
                    const friendly = ({
                        wrong_code: 'That code doesn\'t match. Please check and try again.',
                        code_expired: 'That code has expired. Please request a new one.',
                        bad_token: 'Verification session expired. Please close this dialog and resubmit.',
                        email_mismatch: 'The verified email doesn\'t match the form. Please resubmit.',
                    })[err.message] || `Something went wrong: ${err.message}`;
                    setError(friendly);
                } finally {
                    submitBtn.disabled = false;
                    submitBtn.textContent = 'Verify & submit';
                }
            });
        }

        if (resendBtn) {
            resendBtn.addEventListener('click', async () => {
                if (!pendingPayload) return;
                resendBtn.disabled = true;
                const original = resendBtn.textContent;
                resendBtn.textContent = 'Resending…';
                setError('');
                try {
                    verifyToken = await requestCode(pendingPayload.email);
                    if (codeInput) codeInput.value = '';
                } catch (err) {
                    setError(`Couldn't resend: ${err.message}`);
                } finally {
                    resendBtn.disabled = false;
                    resendBtn.textContent = original;
                }
            });
        }
    }

    // Pre-select the service dropdown when the page is opened with
    // #service=<value> or ?service=<value> (e.g. footer "Support" link →
    // contact.html#service=support). Hash form is used so the value survives
    // the dev server's .html → cleanUrl redirect (which drops query strings).
    const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const fromQuery = new URLSearchParams(window.location.search);
    const requested = fromHash.get('service') || fromQuery.get('service');
    if (requested) {
        const select = document.getElementById('service');
        if (select && [...select.options].some(o => o.value === requested)) {
            select.value = requested;
        }
    }

    // Pre-fill from the chat-widget hand-off. The widget navigates to
    // contact.html#from=chat&reason=...&summary=... (hash form so the dev
    // server's .html→cleanUrl redirect doesn't strip it). We fill the
    // message box with the conversation summary and scroll the form into
    // view. The visitor still completes name/email/phone and verifies.
    const handoffFrom = fromHash.get('from') || fromQuery.get('from');
    if (handoffFrom === 'chat') {
        const summary = fromHash.get('summary') || fromQuery.get('summary') || '';
        const reason = fromHash.get('reason') || fromQuery.get('reason') || '';
        const msgEl = contactForm.querySelector('textarea[name="message"], #message');
        if (msgEl && summary) {
            const preamble = reason === 'pricing-or-quote'
                ? 'Coming from the chatbot — pricing/quote enquiry. Transcript below:\n\n'
                : 'Coming from the chatbot. Transcript below:\n\n';
            msgEl.value = preamble + summary;
        }
        // Scroll the form into view once layout settles.
        requestAnimationFrame(() => {
            contactForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }
}

// FAQ Accordion Functionality
function initFAQAccordions() {
    const accordions = document.querySelectorAll('.accordion');

    accordions.forEach(accordion => {
        const header = accordion.querySelector('.accordion-header');
        const content = accordion.querySelector('.accordion-content');

        if (header && content) {
            header.addEventListener('click', () => {
                const isOpen = accordion.classList.contains('active');

                // Close all other accordions
                accordions.forEach(otherAccordion => {
                    if (otherAccordion !== accordion) {
                        otherAccordion.classList.remove('active');
                        const otherHeader = otherAccordion.querySelector('.accordion-header');
                        if (otherHeader) {
                            otherHeader.setAttribute('aria-expanded', 'false');
                        }
                    }
                });

                // Toggle current accordion
                accordion.classList.toggle('active');
                header.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
            });
        }
    });
}

// Scroll Circle Animation for Services Section
function initScrollCircle() {
    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const scrollCircle = document.querySelector('.scroll-circle circle');
    const servicesSection = document.querySelector('.services-section');

    if (!scrollCircle || !servicesSection) return;

    // Calculate circle circumference for stroke-dasharray (3x larger circle)
    const radius = 420;
    const circumference = 2 * Math.PI * radius; // ≈ 2638.938

    // Set initial state for anti-clockwise drawing
    scrollCircle.style.strokeDasharray = circumference;
    scrollCircle.style.strokeDashoffset = -circumference;

    function updateCircleProgress() {
        const sectionRect = servicesSection.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Calculate how much of the services section is visible
        const sectionTop = sectionRect.top;
        const sectionHeight = sectionRect.height;

        // Start drawing when section comes into view, finish when it's mostly out of view
        // Delay start by 50% of viewport height (increased from 30% to 50% = 20% more delay)
        const startPoint = windowHeight * 0.5;
        const endPoint = -sectionHeight * 0.5;

        // Calculate progress (0 to 1)
        let progress = 0;
        if (sectionTop <= startPoint && sectionTop >= endPoint) {
            progress = (startPoint - sectionTop) / (startPoint - endPoint);
            progress = Math.max(0, Math.min(1, progress)); // Clamp between 0 and 1
        } else if (sectionTop < endPoint) {
            progress = 1;
        }

        // Apply easing for smoother animation
        const easedProgress = progress * progress * (3 - 2 * progress); // Smoothstep

        // Update stroke-dashoffset for anti-clockwise drawing
        // Start from negative full offset, increase toward 0 as we scroll
        const offset = -circumference + (easedProgress * circumference);
        scrollCircle.style.strokeDashoffset = offset;
    }

    // Update on scroll with throttling for performance
    let ticking = false;
    function onScroll() {
        if (!ticking) {
            requestAnimationFrame(() => {
                updateCircleProgress();
                ticking = false;
            });
            ticking = true;
        }
    }

    // Initial call and scroll listener
    updateCircleProgress();
    window.addEventListener('scroll', onScroll, { passive: true });
}

// Second Scroll Circle Animation for Services Section (bottom left)
function initScrollCircle2() {
    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const scrollCircle2 = document.querySelector('.scroll-circle-2 circle');
    const servicesSection = document.querySelector('.services-section');

    if (!scrollCircle2 || !servicesSection) return;

    // Calculate circle circumference for stroke-dasharray (3/4 size of original)
    const radius = 315; // 3/4 of 420
    const circumference = 2 * Math.PI * radius; // ≈ 1979.203

    // Set initial state for clockwise drawing (opposite of first circle)
    scrollCircle2.style.strokeDasharray = circumference;
    scrollCircle2.style.strokeDashoffset = circumference;

    function updateCircle2Progress() {
        const sectionRect = servicesSection.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Calculate how much of the services section is visible
        const sectionTop = sectionRect.top;
        const sectionHeight = sectionRect.height;

        // Start drawing when section comes into view, finish when it's mostly out of view
        // Delay start by 60% of viewport height (increased from 50% to 60% = 10% more delay for bottom circle)
        const startPoint = windowHeight * 0.4;
        const endPoint = -sectionHeight * 0.5;

        // Calculate progress (0 to 1)
        let progress = 0;
        if (sectionTop <= startPoint && sectionTop >= endPoint) {
            progress = (startPoint - sectionTop) / (startPoint - endPoint);
            progress = Math.max(0, Math.min(1, progress)); // Clamp between 0 and 1
        } else if (sectionTop < endPoint) {
            progress = 1;
        }

        // Apply easing for smoother animation
        const easedProgress = progress * progress * (3 - 2 * progress); // Smoothstep

        // Update stroke-dashoffset for clockwise drawing (opposite of first circle)
        // Start from full offset, reduce toward 0 as we scroll
        const offset = circumference - (easedProgress * circumference);
        scrollCircle2.style.strokeDashoffset = offset;
    }

    // Update on scroll with throttling for performance
    let ticking = false;
    function onScroll() {
        if (!ticking) {
            requestAnimationFrame(() => {
                updateCircle2Progress();
                ticking = false;
            });
            ticking = true;
        }
    }

    // Initial call and scroll listener
    updateCircle2Progress();
    window.addEventListener('scroll', onScroll, { passive: true });
}

// Bouncing circles for works section (Pong-style)
function initBouncingCircles() {
    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const worksSection = document.querySelector('.works-section');
    const worksGrid = document.querySelector('.works-grid');
    const circles = [
        document.querySelector('.bounce-circle-1'),
        document.querySelector('.bounce-circle-2'),
        document.querySelector('.bounce-circle-3'),
        document.querySelector('.bounce-circle-4'),
        document.querySelector('.bounce-circle-5')
    ];

    if (!worksSection || !worksGrid || circles.some(circle => !circle)) return;

    // Initialize circle properties with updated sizes (20% larger than original)
    let circleProps = [
        { x: 200, y: 200, vx: (Math.random() - 0.5) * 3.0, vy: (Math.random() - 0.5) * 3.0, size: 224 }, // Updated size: 224px
        { x: 300, y: 300, vx: (Math.random() - 0.5) * 2.5, vy: (Math.random() - 0.5) * 2.5, size: 150 }, // Updated size: 150px
        { x: 150, y: 400, vx: (Math.random() - 0.5) * 4.0, vy: (Math.random() - 0.5) * 4.0, size: 120 }, // Updated size: 120px
        { x: 350, y: 150, vx: (Math.random() - 0.5) * 2.0, vy: (Math.random() - 0.5) * 2.0, size: 180 }, // Updated size: 180px
        { x: 100, y: 350, vx: (Math.random() - 0.5) * 5.0, vy: (Math.random() - 0.5) * 5.0, size: 90 }   // Updated size: 90px
    ];

    // Function to initialize positions outside grid area
    function initializePositions() {
        const worksGridRect = worksGrid.getBoundingClientRect();
        const sectionRect = worksSection.getBoundingClientRect();

        const gridLeft = worksGridRect.left - sectionRect.left;
        const gridTop = worksGridRect.top - sectionRect.top;
        const gridWidth = worksGridRect.width;
        const gridHeight = worksGridRect.height;
        const sectionWidth = sectionRect.width;
        const sectionHeight = sectionRect.height;

        const bottomSpace = sectionHeight - (gridTop + gridHeight);
        const rightSpace = sectionWidth - (gridLeft + gridWidth);

        circleProps.forEach((circle, index) => {
            // Distribute circles in different areas outside the grid
            switch(index % 4) {
                case 0: // Top area
                    if (gridTop > circle.size) {
                        circle.x = Math.random() * (sectionWidth - circle.size) + circle.size / 2;
                        circle.y = Math.random() * (gridTop - circle.size) + circle.size / 2;
                    } else {
                        circle.x = Math.random() * (gridLeft - circle.size) + circle.size / 2;
                        circle.y = Math.random() * (sectionHeight - circle.size) + circle.size / 2;
                    }
                    break;
                case 1: // Bottom area
                    if (bottomSpace > circle.size) {
                        circle.x = Math.random() * (sectionWidth - circle.size) + circle.size / 2;
                        circle.y = gridTop + gridHeight + Math.random() * (bottomSpace - circle.size) + circle.size / 2;
                    } else {
                        circle.x = gridLeft + gridWidth + Math.random() * (rightSpace - circle.size) + circle.size / 2;
                        circle.y = Math.random() * (sectionHeight - circle.size) + circle.size / 2;
                    }
                    break;
                case 2: // Left area
                    if (gridLeft > circle.size) {
                        circle.x = Math.random() * (gridLeft - circle.size) + circle.size / 2;
                        circle.y = Math.random() * (sectionHeight - circle.size) + circle.size / 2;
                    } else {
                        circle.x = Math.random() * (sectionWidth - circle.size) + circle.size / 2;
                        circle.y = Math.random() * (gridTop - circle.size) + circle.size / 2;
                    }
                    break;
                case 3: // Right area
                    if (rightSpace > circle.size) {
                        circle.x = gridLeft + gridWidth + Math.random() * (rightSpace - circle.size) + circle.size / 2;
                        circle.y = Math.random() * (sectionHeight - circle.size) + circle.size / 2;
                    } else {
                        circle.x = Math.random() * (sectionWidth - circle.size) + circle.size / 2;
                        circle.y = gridTop + gridHeight + Math.random() * (bottomSpace - circle.size) + circle.size / 2;
                    }
                    break;
            }
        });
    }

    function updatePositions() {
        const worksGridRect = worksGrid.getBoundingClientRect();
        const sectionRect = worksSection.getBoundingClientRect();

        // Calculate grid boundaries relative to the section
        const gridLeft = worksGridRect.left - sectionRect.left;
        const gridTop = worksGridRect.top - sectionRect.top;
        const gridWidth = worksGridRect.width;
        const gridHeight = worksGridRect.height;
        const sectionWidth = sectionRect.width;
        const sectionHeight = sectionRect.height;

        // Update all circles
        circleProps.forEach((circle, index) => {
            // Update position
            circle.x += circle.vx;
            circle.y += circle.vy;

            // Bounce off section walls
            if (circle.x <= circle.size / 2 || circle.x >= sectionWidth - circle.size / 2) {
                circle.vx = -circle.vx;
                circle.x = Math.max(circle.size / 2, Math.min(sectionWidth - circle.size / 2, circle.x));
            }
            if (circle.y <= circle.size / 2 || circle.y >= sectionHeight - circle.size / 2) {
                circle.vy = -circle.vy;
                circle.y = Math.max(circle.size / 2, Math.min(sectionHeight - circle.size / 2, circle.y));
            }

            // Bounce off grid area (treat grid as obstacle)
            if (circle.x + circle.size / 2 > gridLeft &&
                circle.x - circle.size / 2 < gridLeft + gridWidth &&
                circle.y + circle.size / 2 > gridTop &&
                circle.y - circle.size / 2 < gridTop + gridHeight) {

                // Calculate overlaps with each side
                const overlapLeft = (circle.x + circle.size / 2) - gridLeft;
                const overlapRight = (gridLeft + gridWidth) - (circle.x - circle.size / 2);
                const overlapTop = (circle.y + circle.size / 2) - gridTop;
                const overlapBottom = (gridTop + gridHeight) - (circle.y - circle.size / 2);

                // Find the smallest overlap to determine collision side
                const minOverlap = Math.min(overlapLeft, overlapRight, overlapTop, overlapBottom);

                // Bounce based on the side with minimum overlap
                if (minOverlap === overlapLeft && circle.vx > 0) {
                    circle.vx = -circle.vx;
                } else if (minOverlap === overlapRight && circle.vx < 0) {
                    circle.vx = -circle.vx;
                } else if (minOverlap === overlapTop && circle.vy > 0) {
                    circle.vy = -circle.vy;
                } else if (minOverlap === overlapBottom && circle.vy < 0) {
                    circle.vy = -circle.vy;
                }

                // For corner collisions, reverse both velocities
                if ((minOverlap === overlapLeft || minOverlap === overlapRight) &&
                    (minOverlap === overlapTop || minOverlap === overlapBottom)) {
                    circle.vx = -circle.vx;
                    circle.vy = -circle.vy;
                }
            }

            // Apply position to DOM element
            circles[index].style.left = `${circle.x - circle.size / 2}px`;
            circles[index].style.top = `${circle.y - circle.size / 2}px`;
        });
    }

    // Start animation
    function animate() {
        updatePositions();
        requestAnimationFrame(animate);
    }

    // Initialize positions within grid
    initializePositions();
    animate();
}

// Hero Card Scroll Animation
function initHeroCardScrollAnimation() {
    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const heroCard = document.querySelector('.hero-card');
    if (!heroCard) return;

    function updateHeroCardState() {
        const scrollPosition = window.scrollY;

        if (scrollPosition >= 100) {
            heroCard.classList.add('scroll-activated');
        } else {
            heroCard.classList.remove('scroll-activated');
        }
    }

    // Update on scroll with throttling for performance
    let ticking = false;
    function onScroll() {
        if (!ticking) {
            requestAnimationFrame(() => {
                updateHeroCardState();
                ticking = false;
            });
            ticking = true;
        }
    }

    // Initial call and scroll listener
    updateHeroCardState();
    window.addEventListener('scroll', onScroll, { passive: true });
}

// Hero Stats Count-Up Animation
function initStatsCountUp() {
    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const statNumbers = document.querySelectorAll('.hero-stats .stat-number');
    if (!statNumbers.length) return;

    const duration = 2000; // 2 seconds (reduced from 4)

    statNumbers.forEach(statElement => {
        const text = statElement.textContent;
        const hasPercent = text.includes('%');
        const hasPlus = text.includes('+');
        const targetValue = parseFloat(text);

        if (isNaN(targetValue)) return;

        let hasAnimated = false;

        // Easing function for smooth animation
        function easeOutQuart(x) {
            return 1 - Math.pow(1 - x, 4);
        }

        function animateCounter() {
            if (hasAnimated) return;
            hasAnimated = true;

            const startTime = performance.now();

            function updateCount(currentTime) {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easedProgress = easeOutQuart(progress);
                const currentValue = easedProgress * targetValue;

                // Format the number based on original format
                let displayValue;
                if (targetValue % 1 === 0) {
                    // Integer
                    displayValue = Math.round(currentValue);
                } else {
                    // Decimal
                    displayValue = currentValue.toFixed(0);
                }

                // Add suffix back
                statElement.textContent = displayValue + (hasPercent ? '%' : '') + (hasPlus ? '+' : '');

                if (progress < 1) {
                    requestAnimationFrame(updateCount);
                }
            }

            requestAnimationFrame(updateCount);
        }

        // Use Intersection Observer to trigger animation when in viewport
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    animateCounter();
                    observer.unobserve(statElement);
                }
            });
        }, {
            threshold: 0.5
        });

        observer.observe(statElement);
    });
}

// Text Accent Letter Unfurl Animation
function initTextAccentUnfurl() {
    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const accentSpans = document.querySelectorAll('.text-accent, h1 em, h2 em, h3 em');
    if (!accentSpans.length) return;

    const pendingSpans = [];

    const isInViewport = (el) => {
        const rect = el.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
    };

    const revealLetters = (span) => {
        span.querySelectorAll('span').forEach(letterSpan => {
            letterSpan.style.opacity = '1';
            letterSpan.style.transform = 'translateY(0)';
        });
    };

    accentSpans.forEach(span => {
        const text = span.textContent;
        const letters = text.split('');

        span.textContent = '';

        letters.forEach((letter, index) => {
            const letterSpan = document.createElement('span');
            letterSpan.textContent = letter === ' ' ? '\u00A0' : letter;
            letterSpan.style.display = 'inline-block';
            letterSpan.style.opacity = '0';
            letterSpan.style.transform = 'translateY(20px)';
            letterSpan.style.transition = `opacity 0.3s ease ${index * 0.08}s, transform 0.3s ease ${index * 0.08}s`;
            span.appendChild(letterSpan);
        });

        if (isInViewport(span)) {
            setTimeout(() => revealLetters(span), 100);
        } else {
            pendingSpans.push(span);
        }
    });

    if (pendingSpans.length) {
        const checkPending = () => {
            for (let i = pendingSpans.length - 1; i >= 0; i--) {
                if (isInViewport(pendingSpans[i])) {
                    revealLetters(pendingSpans[i]);
                    pendingSpans.splice(i, 1);
                }
            }
            if (!pendingSpans.length) {
                window.removeEventListener('scroll', checkPending);
                window.removeEventListener('load', checkPending);
            }
        };
        window.addEventListener('scroll', checkPending, { passive: true });
        // Second pass once all resources are loaded — catches spans missed due to layout not being complete at DOMContentLoaded
        window.addEventListener('load', checkPending);
    }
}

// Initialize all functionality when DOM is loaded
// ==========================================================================
// SKILLS CAROUSEL - Infinite Scroll
// ==========================================================================
function initSkillsCarousel() {
    const carousel = document.querySelector('.skills-carousel');
    if (!carousel) return;

    // Duplicate items for infinite scroll effect
    const items = Array.from(carousel.children);
    items.forEach(item => {
        const clone = item.cloneNode(true);
        carousel.appendChild(clone);
    });
}

// ==========================================================================
// PARALLAX EFFECT MODAL
// ==========================================================================
function initParallaxModal() {
    const modal = document.getElementById('parallax-modal');
    const closeBtn = modal?.querySelector('.parallax-modal-close');
    const overlay = modal?.querySelector('.parallax-modal-overlay');
    const parallaxContainers = modal?.querySelectorAll('.parallax-container');
    const prevBtn = modal?.querySelector('.parallax-prev');
    const nextBtn = modal?.querySelector('.parallax-next');
    const dots = modal?.querySelectorAll('.parallax-dot');

    if (!modal) return;

    let currentIndex = 0;

    // Respect reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Show specific parallax scene
    function showParallaxScene(index) {
        parallaxContainers?.forEach((container, i) => {
            container.classList.toggle('active', i === index);
        });
        dots?.forEach((dot, i) => {
            dot.classList.toggle('active', i === index);
        });
        currentIndex = index;
    }

    // Next/Prev navigation
    prevBtn?.addEventListener('click', () => {
        const newIndex = (currentIndex - 1 + parallaxContainers.length) % parallaxContainers.length;
        showParallaxScene(newIndex);
    });

    nextBtn?.addEventListener('click', () => {
        const newIndex = (currentIndex + 1) % parallaxContainers.length;
        showParallaxScene(newIndex);
    });

    // Dot navigation
    dots?.forEach((dot, index) => {
        dot.addEventListener('click', () => {
            showParallaxScene(index);
        });
    });

    // Open modal using event delegation (works with cloned carousel items)
    document.addEventListener('click', (e) => {
        const parallaxSkill = e.target.closest('#parallax-skill');
        if (parallaxSkill) {
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
            showParallaxScene(0); // Reset to first scene
        }
    });

    // Close modal function
    const closeModal = () => {
        modal.classList.remove('active');
        document.body.style.overflow = '';
    };

    // Close on button click
    closeBtn?.addEventListener('click', closeModal);

    // Close on overlay click
    overlay?.addEventListener('click', closeModal);

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            closeModal();
        }
    });

    // Parallax effect on mouse move for ALL containers using smooth interpolation
    if (!prefersReducedMotion && parallaxContainers) {
        parallaxContainers.forEach(parallaxContainer => {
            const layers = parallaxContainer.querySelectorAll('.parallax-layer');

        // Store current and target positions for smooth interpolation
        const layerPositions = Array.from(layers).map(() => ({
            currentX: 0,
            currentY: 0,
            targetX: 0,
            targetY: 0
        }));

        let animationFrameId = null;

        // Smooth animation loop
        function animate() {
            let needsUpdate = false;

            layerPositions.forEach((pos, index) => {
                // Smoothly interpolate towards target position
                const ease = 0.1; // Lower = smoother but slower, higher = faster but less smooth
                pos.currentX += (pos.targetX - pos.currentX) * ease;
                pos.currentY += (pos.targetY - pos.currentY) * ease;

                // Apply transform
                layers[index].style.transform = `translate(
                    calc(-50% + ${pos.currentX}px),
                    calc(-50% + ${pos.currentY}px)
                )`;

                // Check if still moving
                if (Math.abs(pos.targetX - pos.currentX) > 0.1 || Math.abs(pos.targetY - pos.currentY) > 0.1) {
                    needsUpdate = true;
                }
            });

            if (needsUpdate) {
                animationFrameId = requestAnimationFrame(animate);
            } else {
                animationFrameId = null; // Reset so animation can restart
            }
        }

        // Function to update parallax based on position
        function updateParallax(clientX, clientY) {
            const rect = parallaxContainer.getBoundingClientRect();
            const x = (clientX - rect.left) / rect.width;
            const y = (clientY - rect.top) / rect.height;

            // Center coordinates from -0.5 to 0.5
            const centerX = x - 0.5;
            const centerY = y - 0.5;

            // Update target positions for each layer
            layers.forEach((layer, index) => {
                const speed = parseInt(layer.dataset.speed) || 20;
                layerPositions[index].targetX = centerX * speed;
                layerPositions[index].targetY = centerY * speed;
            });

            // Start animation if not already running
            if (!animationFrameId) {
                animationFrameId = requestAnimationFrame(animate);
            }
        }

        // Function to reset parallax
        function resetParallax() {
            layerPositions.forEach(pos => {
                pos.targetX = 0;
                pos.targetY = 0;
            });

            if (!animationFrameId) {
                animationFrameId = requestAnimationFrame(animate);
            }
        }

        // Mouse events
        parallaxContainer.addEventListener('mousemove', (e) => {
            updateParallax(e.clientX, e.clientY);
        });

        parallaxContainer.addEventListener('mouseleave', () => {
            resetParallax();
        });

        // Touch events for mobile
        parallaxContainer.addEventListener('touchmove', (e) => {
            e.preventDefault(); // Prevent scrolling while touching
            const touch = e.touches[0];
            updateParallax(touch.clientX, touch.clientY);
        }, { passive: false });

        parallaxContainer.addEventListener('touchend', () => {
            resetParallax();
        });
        }); // Close forEach for parallaxContainers
    }
}

// ==========================================================================
// TILT SHIFT MODAL WITH CAROUSEL
// ==========================================================================
function initTiltShiftModal() {
    const modal = document.getElementById('tiltshift-modal');
    const closeBtn = modal?.querySelector('.parallax-modal-close');
    const overlay = modal?.querySelector('.parallax-modal-overlay');
    const images = modal?.querySelectorAll('.tiltshift-image');
    const dots = modal?.querySelectorAll('.parallax-dot');
    const prevBtn = modal?.querySelector('.parallax-prev');
    const nextBtn = modal?.querySelector('.parallax-next');

    if (!modal || !images || images.length === 0) return;

    let currentIndex = 0;

    // Show image at given index
    const showImage = (index) => {
        images.forEach((img, i) => {
            img.classList.toggle('active', i === index);
        });
        dots.forEach((dot, i) => {
            dot.classList.toggle('active', i === index);
        });
        currentIndex = index;
    };

    // Navigate to previous image
    const prevImage = () => {
        const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
        showImage(newIndex);
    };

    // Navigate to next image
    const nextImage = () => {
        const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
        showImage(newIndex);
    };

    // Event listeners for navigation
    prevBtn?.addEventListener('click', prevImage);
    nextBtn?.addEventListener('click', nextImage);

    // Event listeners for dots
    dots.forEach((dot, index) => {
        dot.addEventListener('click', () => showImage(index));
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
        if (!modal.classList.contains('active')) return;

        if (e.key === 'ArrowLeft') {
            prevImage();
        } else if (e.key === 'ArrowRight') {
            nextImage();
        }
    });

    // Open modal using event delegation (works with cloned carousel items)
    document.addEventListener('click', (e) => {
        const tiltshiftSkill = e.target.closest('#tiltshift-skill');
        if (tiltshiftSkill) {
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
            showImage(0); // Reset to first image
        }
    });

    // Close modal function
    const closeModal = () => {
        modal.classList.remove('active');
        document.body.style.overflow = '';
    };

    // Close on button click
    closeBtn?.addEventListener('click', closeModal);

    // Close on overlay click
    overlay?.addEventListener('click', closeModal);

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            closeModal();
        }
    });
}

// ==========================================================================
// PRODUCT PLACEMENT MODAL WITH CAROUSEL
// ==========================================================================
function initProductPlacementModal() {
    const modal = document.getElementById('product-placement-modal');
    const closeBtn = modal?.querySelector('.parallax-modal-close');
    const overlay = modal?.querySelector('.parallax-modal-overlay');
    const images = modal?.querySelectorAll('.tiltshift-image');
    const dots = modal?.querySelectorAll('.parallax-dot');
    const prevBtn = modal?.querySelector('.parallax-prev');
    const nextBtn = modal?.querySelector('.parallax-next');

    if (!modal || !images || images.length === 0) return;

    let currentIndex = 0;

    // Show image at given index
    const showImage = (index) => {
        images.forEach((img, i) => {
            img.classList.toggle('active', i === index);
        });
        dots.forEach((dot, i) => {
            dot.classList.toggle('active', i === index);
        });
        currentIndex = index;
    };

    // Navigate to previous image
    const prevImage = () => {
        const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
        showImage(newIndex);
    };

    // Navigate to next image
    const nextImage = () => {
        const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
        showImage(newIndex);
    };

    // Event listeners for navigation
    prevBtn?.addEventListener('click', prevImage);
    nextBtn?.addEventListener('click', nextImage);

    // Event listeners for dots
    dots.forEach((dot, index) => {
        dot.addEventListener('click', () => showImage(index));
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
        if (!modal.classList.contains('active')) return;

        if (e.key === 'ArrowLeft') {
            prevImage();
        } else if (e.key === 'ArrowRight') {
            nextImage();
        }
    });

    // Open modal using event delegation
    document.addEventListener('click', (e) => {
        const productPlacementSkill = e.target.closest('#product-placement-skill');
        if (productPlacementSkill) {
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
            showImage(0); // Reset to first image
        }
    });

    // Close modal function
    const closeModal = () => {
        modal.classList.remove('active');
        document.body.style.overflow = '';
    };

    // Close on button click
    closeBtn?.addEventListener('click', closeModal);

    // Close on overlay click
    overlay?.addEventListener('click', closeModal);

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            closeModal();
        }
    });
}

// ==========================================================================
// 3D MODEL VIEWER MODAL WITH CAROUSEL
// ==========================================================================

function init3DViewerModal() {
    const modal = document.getElementById('3d-viewer-modal');
    const closeBtn = modal?.querySelector('.parallax-modal-close');
    const overlay = modal?.querySelector('.parallax-modal-overlay');
    const prevBtn = modal?.querySelector('.model-prev');
    const nextBtn = modal?.querySelector('.model-next');
    const models = modal?.querySelectorAll('.model-item');
    const dots = modal?.querySelectorAll('.model-indicators .parallax-dot');

    if (!modal) return;

    let currentIndex = 0;

    // Function to show specific model
    const showModel = (index) => {
        // Remove active class from all models and dots
        models?.forEach(model => model.classList.remove('active'));
        dots?.forEach(dot => dot.classList.remove('active'));

        // Add active class to current model and dot
        if (models && models[index]) {
            models[index].classList.add('active');
        }
        if (dots && dots[index]) {
            dots[index].classList.add('active');
        }

        currentIndex = index;
    };

    // Navigate to previous model
    const showPrevious = () => {
        const newIndex = currentIndex > 0 ? currentIndex - 1 : models.length - 1;
        showModel(newIndex);
    };

    // Navigate to next model
    const showNext = () => {
        const newIndex = currentIndex < models.length - 1 ? currentIndex + 1 : 0;
        showModel(newIndex);
    };

    // Previous button click
    prevBtn?.addEventListener('click', showPrevious);

    // Next button click
    nextBtn?.addEventListener('click', showNext);

    // Dot click navigation
    dots?.forEach(dot => {
        dot.addEventListener('click', () => {
            const index = parseInt(dot.getAttribute('data-index') || '0');
            showModel(index);
        });
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
        if (!modal.classList.contains('active')) return;

        if (e.key === 'ArrowLeft') {
            showPrevious();
        } else if (e.key === 'ArrowRight') {
            showNext();
        }
    });

    // Open modal using event delegation (supports cloned carousel items)
    document.addEventListener('click', (e) => {
        const viewerSkill = e.target.closest('#viewer-3d-skill');
        if (viewerSkill) {
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
            // Reset to first model when opening
            showModel(0);
        }
    });

    // Close modal function
    const closeModal = () => {
        modal.classList.remove('active');
        document.body.style.overflow = '';
    };

    // Close on button click
    closeBtn?.addEventListener('click', closeModal);

    // Close on overlay click
    overlay?.addEventListener('click', closeModal);

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            closeModal();
        }
    });
}

// ==========================================================================
// AR QR CODE MODAL
// ==========================================================================

function initARQRModal() {
    const modal = document.getElementById('ar-qr-modal');
    const closeBtn = modal?.querySelector('.parallax-modal-close');
    const overlay = modal?.querySelector('.parallax-modal-overlay');

    if (!modal) return;

    // Open modal using event delegation (supports cloned carousel items)
    document.addEventListener('click', (e) => {
        const arSkill = e.target.closest('#ar-skill');
        if (arSkill) {
            // Check if mobile - direct link to AR page
            if (window.innerWidth <= 768) {
                window.location.href = 'ar-experience.html';
            } else {
                // Desktop - show QR code modal
                modal.classList.add('active');
                document.body.style.overflow = 'hidden';
            }
        }
    });

    // Close modal function
    const closeModal = () => {
        modal.classList.remove('active');
        document.body.style.overflow = '';
    };

    // Close on button click
    closeBtn?.addEventListener('click', closeModal);

    // Close on overlay click
    overlay?.addEventListener('click', closeModal);

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            closeModal();
        }
    });
}

// ==========================================================================
// 360 DEGREE IMAGES MODAL
// ==========================================================================
function init360ImagesModal() {
    const modal = document.getElementById('360-images-modal');
    if (!modal) {
        console.log('360 images modal not found');
        return;
    }

    const overlay = modal.querySelector('.parallax-modal-overlay');
    const closeBtn = modal.querySelector('.parallax-modal-close');

    // Open modal using event delegation (works with cloned carousel items)
    document.addEventListener('click', (e) => {
        const skill360 = e.target.closest('#images-360-skill');
        if (skill360) {
            console.log('360 images skill clicked, opening modal');
            modal.classList.add('active');
        }
    });

    // Close modal functions
    const closeModal = () => {
        modal.classList.remove('active');
    };

    closeBtn?.addEventListener('click', closeModal);
    overlay?.addEventListener('click', closeModal);

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            closeModal();
        }
    });
}

// ==========================================================================
// IMAGE TO VIDEO MODAL
// ==========================================================================
function initImageToVideoModal() {
    const modal = document.getElementById('image-to-video-modal');
    const closeBtn = modal?.querySelector('.parallax-modal-close');
    const overlay = modal?.querySelector('.parallax-modal-overlay');
    const video = modal?.querySelector('video');

    if (!modal) return;

    // Open modal using event delegation
    document.addEventListener('click', (e) => {
        const imageToVideoSkill = e.target.closest('#image-to-video-skill');
        if (imageToVideoSkill) {
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
            // Play video when modal opens
            if (video) {
                video.currentTime = 0; // Reset to start
                video.play();
            }
        }
    });

    // Close modal function
    const closeModal = () => {
        modal.classList.remove('active');
        document.body.style.overflow = '';
        // Pause video when modal closes
        if (video) {
            video.pause();
        }
    };

    // Close on button click
    closeBtn?.addEventListener('click', closeModal);

    // Close on overlay click
    overlay?.addEventListener('click', closeModal);

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            closeModal();
        }
    });
}

// ==========================================================================
// AI AVATARS MODAL
// ==========================================================================
function initAiAvatarsModal() {
    const modal = document.getElementById('ai-avatars-modal');
    const closeBtn = modal?.querySelector('.parallax-modal-close');
    const overlay = modal?.querySelector('.parallax-modal-overlay');
    const video = modal?.querySelector('video');

    if (!modal) return;

    // Open modal using event delegation (works on carousel clones too)
    document.addEventListener('click', (e) => {
        const aiAvatarsSkill = e.target.closest('#ai-avatars-skill');
        if (aiAvatarsSkill) {
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
            // Play video (with cloned-voice audio) when modal opens
            if (video) {
                video.currentTime = 0; // Reset to start
                video.play();
            }
        }
    });

    // Close modal function
    const closeModal = () => {
        modal.classList.remove('active');
        document.body.style.overflow = '';
        // Pause video when modal closes
        if (video) {
            video.pause();
        }
    };

    // Close on button click
    closeBtn?.addEventListener('click', closeModal);

    // Close on overlay click
    overlay?.addEventListener('click', closeModal);

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            closeModal();
        }
    });
}

// ==========================================================================
// BRAND DEVELOPMENT MODAL
// ==========================================================================
function initBrandDevelopmentModal() {
    const modal = document.getElementById('brand-development-modal');
    const closeBtn = modal?.querySelector('.parallax-modal-close');
    const overlay = modal?.querySelector('.parallax-modal-overlay');
    const prevBtn = modal?.querySelector('.parallax-prev');
    const nextBtn = modal?.querySelector('.parallax-next');
    const slides = modal?.querySelectorAll('.tiltshift-image, .brand-video-slide');
    const dots = modal?.querySelectorAll('.parallax-dot');

    if (!modal) return;

    let currentIndex = 0;

    // Function to show specific slide
    const showSlide = (index) => {
        // Remove active class from all slides and dots
        slides?.forEach(slide => slide.classList.remove('active'));
        dots?.forEach(dot => dot.classList.remove('active'));

        // Add active class to current slide and dot
        if (slides && slides[index]) {
            slides[index].classList.add('active');
            // If it's a video slide, play the video
            const video = slides[index].querySelector('video');
            if (video) {
                video.currentTime = 0;
                video.play();
            }
        }
        if (dots && dots[index]) {
            dots[index].classList.add('active');
        }

        // Pause videos on inactive slides
        slides?.forEach((slide, idx) => {
            if (idx !== index) {
                const video = slide.querySelector('video');
                if (video) {
                    video.pause();
                }
            }
        });

        currentIndex = index;
    };

    // Navigate to previous slide
    const showPrevious = () => {
        const newIndex = currentIndex > 0 ? currentIndex - 1 : slides.length - 1;
        showSlide(newIndex);
    };

    // Navigate to next slide
    const showNext = () => {
        const newIndex = currentIndex < slides.length - 1 ? currentIndex + 1 : 0;
        showSlide(newIndex);
    };

    // Previous button click
    prevBtn?.addEventListener('click', showPrevious);

    // Next button click
    nextBtn?.addEventListener('click', showNext);

    // Dot click navigation
    dots?.forEach(dot => {
        dot.addEventListener('click', () => {
            const index = parseInt(dot.getAttribute('data-index') || '0');
            showSlide(index);
        });
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
        if (!modal.classList.contains('active')) return;

        if (e.key === 'ArrowLeft') {
            showPrevious();
        } else if (e.key === 'ArrowRight') {
            showNext();
        }
    });

    // Open modal using event delegation
    document.addEventListener('click', (e) => {
        const brandDevelopmentSkill = e.target.closest('#brand-development-skill');
        if (brandDevelopmentSkill) {
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
            showSlide(0); // Reset to first slide
        }
    });

    // Close modal function
    const closeModal = () => {
        modal.classList.remove('active');
        document.body.style.overflow = '';
        // Pause all videos when modal closes
        slides?.forEach(slide => {
            const video = slide.querySelector('video');
            if (video) {
                video.pause();
            }
        });
    };

    // Close on button click
    closeBtn?.addEventListener('click', closeModal);

    // Close on overlay click
    overlay?.addEventListener('click', closeModal);

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            closeModal();
        }
    });
}

// ==========================================================================
// PHOTO MANIPULATION MODAL
// ==========================================================================
function initPhotoManipulationModal() {
    const modal = document.getElementById('photo-manipulation-modal');
    const closeBtn = modal?.querySelector('.parallax-modal-close');
    const overlay = modal?.querySelector('.parallax-modal-overlay');
    const prevBtn = modal?.querySelector('.parallax-prev');
    const nextBtn = modal?.querySelector('.parallax-next');
    const slides = modal?.querySelectorAll('.tiltshift-image, .brand-video-slide');
    const dots = modal?.querySelectorAll('.parallax-dot');

    if (!modal) return;

    let currentIndex = 0;

    // Function to show specific slide
    const showSlide = (index) => {
        // Remove active class from all slides and dots
        slides?.forEach(slide => slide.classList.remove('active'));
        dots?.forEach(dot => dot.classList.remove('active'));

        // Add active class to current slide and dot
        if (slides && slides[index]) {
            slides[index].classList.add('active');
            // If it's a video slide, play the video
            const video = slides[index].querySelector('video');
            if (video) {
                video.currentTime = 0;
                video.play();
            }
        }
        if (dots && dots[index]) {
            dots[index].classList.add('active');
        }

        // Pause videos on inactive slides
        slides?.forEach((slide, idx) => {
            if (idx !== index) {
                const video = slide.querySelector('video');
                if (video) {
                    video.pause();
                }
            }
        });

        currentIndex = index;
    };

    // Navigate to previous slide
    const showPrevious = () => {
        const newIndex = currentIndex > 0 ? currentIndex - 1 : slides.length - 1;
        showSlide(newIndex);
    };

    // Navigate to next slide
    const showNext = () => {
        const newIndex = currentIndex < slides.length - 1 ? currentIndex + 1 : 0;
        showSlide(newIndex);
    };

    // Previous button click
    prevBtn?.addEventListener('click', showPrevious);

    // Next button click
    nextBtn?.addEventListener('click', showNext);

    // Dot click navigation
    dots?.forEach(dot => {
        dot.addEventListener('click', () => {
            const index = parseInt(dot.getAttribute('data-index') || '0');
            showSlide(index);
        });
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
        if (!modal.classList.contains('active')) return;

        if (e.key === 'ArrowLeft') {
            showPrevious();
        } else if (e.key === 'ArrowRight') {
            showNext();
        }
    });

    // Open modal using event delegation
    document.addEventListener('click', (e) => {
        const photoManipulationSkill = e.target.closest('#photo-manipulation-skill');
        if (photoManipulationSkill) {
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
            showSlide(0); // Reset to first slide
        }
    });

    // Close modal function
    const closeModal = () => {
        modal.classList.remove('active');
        document.body.style.overflow = '';
        // Pause all videos when modal closes
        slides?.forEach(slide => {
            const video = slide.querySelector('video');
            if (video) {
                video.pause();
            }
        });
    };

    // Close on button click
    closeBtn?.addEventListener('click', closeModal);

    // Close on overlay click
    overlay?.addEventListener('click', closeModal);

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            closeModal();
        }
    });
}

// ==========================================================================
// ANIMATION MODAL — logo reveal carousel
// ==========================================================================
function initAnimationModal() {
    const modal = document.getElementById('animation-modal');
    if (!modal) return;

    const closeBtn = modal.querySelector('.parallax-modal-close');
    const overlay = modal.querySelector('.parallax-modal-overlay');
    const replayBtn = modal.querySelector('.anim-modal-replay-btn');
    const prevBtn = modal.querySelector('.animation-prev');
    const nextBtn = modal.querySelector('.animation-next');
    const slides = modal.querySelectorAll('.animation-slide');
    const dots = modal.querySelectorAll('.animation-indicators .parallax-dot');
    const wrappers = modal.querySelectorAll('.anim-modal-logo-wrapper');

    let currentIndex = 0;

    const playAnimation = (wrapper) => {
        if (!wrapper) return;
        wrapper.classList.remove('logo-anim-active');
        void wrapper.offsetWidth; // force reflow to restart CSS animation
        wrapper.classList.add('logo-anim-active');

        // Restart SVG SMIL animations on the i-dot bounce variant. The two
        // dots use staggered delays (200ms / 550ms) so the second dot starts
        // partway through the first.
        if (wrapper.dataset.variant === 'dots') {
            const startSmil = (group, delay) => {
                if (!group) return;
                const animations = group.querySelectorAll('animateTransform, animate');
                animations.forEach(anim => {
                    if (typeof anim.beginElementAt === 'function') {
                        anim.beginElementAt(delay / 1000);
                    }
                });
            };
            const dot1 = wrapper.querySelector('.lc-dot-1');
            const dot2 = wrapper.querySelector('.lc-dot-2');
            startSmil(dot1, 200);
            startSmil(dot2, 550);
        }
    };

    const showSlide = (index) => {
        currentIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('active', i === currentIndex));
        dots.forEach((dot, i) => dot.classList.toggle('active', i === currentIndex));
        // Reset all wrappers, then play the active one
        wrappers.forEach(w => w.classList.remove('logo-anim-active'));
        playAnimation(wrappers[currentIndex]);
    };

    const showPrev = () => showSlide(currentIndex - 1);
    const showNext = () => showSlide(currentIndex + 1);

    // Open modal via event delegation (works with cloned carousel items)
    document.addEventListener('click', (e) => {
        const animSkill = e.target.closest('#animation-skill');
        if (animSkill) {
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
            setTimeout(() => showSlide(0), 120);
        }
    });

    const closeModal = () => {
        modal.classList.remove('active');
        document.body.style.overflow = '';
        wrappers.forEach(w => w.classList.remove('logo-anim-active'));
    };

    closeBtn?.addEventListener('click', closeModal);
    overlay?.addEventListener('click', closeModal);
    prevBtn?.addEventListener('click', showPrev);
    nextBtn?.addEventListener('click', showNext);

    dots.forEach((dot, i) => {
        dot.addEventListener('click', () => showSlide(i));
    });

    replayBtn?.addEventListener('click', () => playAnimation(wrappers[currentIndex]));

    document.addEventListener('keydown', (e) => {
        if (!modal.classList.contains('active')) return;
        if (e.key === 'Escape') closeModal();
        else if (e.key === 'ArrowLeft') showPrev();
        else if (e.key === 'ArrowRight') showNext();
    });
}

// AI Chatbots modal — animated natiive Assistant demo. The whole sequence is
// a CSS timeline; JS just opens/closes the modal and (re)starts the timeline
// by toggling .chat-mock--play with a reflow so each open replays from frame 0.
function initAIChatbotsModal() {
    const modal = document.getElementById('ai-chatbots-modal');
    if (!modal) return;

    const closeBtn = modal.querySelector('.parallax-modal-close');
    const overlay = modal.querySelector('.parallax-modal-overlay');
    const replayBtn = modal.querySelector('.ai-chatbot-replay');
    const mock = modal.querySelector('.chat-mock');

    const play = () => {
        if (!mock) return;
        mock.classList.remove('chat-mock--play');
        void mock.offsetWidth; // force reflow to restart the CSS animations
        mock.classList.add('chat-mock--play');
    };

    // Open via event delegation (works with cloned carousel items)
    document.addEventListener('click', (e) => {
        if (e.target.closest('#ai-chatbots-skill')) {
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
            setTimeout(play, 120);
        }
    });

    const closeModal = () => {
        modal.classList.remove('active');
        document.body.style.overflow = '';
        mock?.classList.remove('chat-mock--play');
    };

    closeBtn?.addEventListener('click', closeModal);
    overlay?.addEventListener('click', closeModal);
    replayBtn?.addEventListener('click', play);

    document.addEventListener('keydown', (e) => {
        if (modal.classList.contains('active') && e.key === 'Escape') closeModal();
    });
}

// ==========================================================================
// MEGA MENU — desktop Services hover panel
// ==========================================================================
function initMegaMenu() {
    const navItem   = document.getElementById('servicesNavItem');
    const panel     = document.getElementById('megaPanel');
    if (!navItem || !panel) return;

    const backdrop  = document.getElementById('megaBackdrop');
    const tabs      = panel.querySelectorAll('.mega-tab');
    const subPanels = panel.querySelectorAll('.mega-sub-panel');
    const polFrames = panel.querySelectorAll('.mega-pol-frame');
    const svcSlots  = panel.querySelectorAll('.pol-service-slot');
    const subLinks  = panel.querySelectorAll('.mega-sub-link');

    let closeTimer;
    let activeService = 'web-design';

    function openPanel() {
        clearTimeout(closeTimer);
        panel.classList.add('open');
        navItem.classList.add('mega-open');
        if (backdrop) backdrop.classList.add('open');
    }

    function closePanel() {
        closeTimer = setTimeout(() => {
            panel.classList.remove('open');
            navItem.classList.remove('mega-open');
            if (backdrop) backdrop.classList.remove('open');
        }, 140);
    }

    function showPolaroid(pol) {
        polFrames.forEach(f => f.classList.toggle('active', f.dataset.pol === pol));
    }

    function activateService(service) {
        activeService = service;
        tabs.forEach(t => t.classList.toggle('active', t.dataset.service === service));
        subPanels.forEach(p => p.classList.toggle('active', p.dataset.service === service));
        svcSlots.forEach(s => s.classList.toggle('active', s.dataset.service === service));
        showPolaroid(service);
    }

    // Open on hover of nav item or panel (with close delay to bridge the gap)
    navItem.addEventListener('mouseenter', () => {
        if (window.innerWidth > 900) openPanel();
    });
    navItem.addEventListener('mouseleave', () => {
        if (window.innerWidth > 900) closePanel();
    });
    panel.addEventListener('mouseenter', openPanel);
    panel.addEventListener('mouseleave', closePanel);

    // Tab hover → switch active service
    tabs.forEach(tab => {
        tab.addEventListener('mouseenter', () => activateService(tab.dataset.service));
    });

    // Sub-link hover → swap right polaroid; revert on leave
    subLinks.forEach(link => {
        link.addEventListener('mouseenter', () => showPolaroid(link.dataset.polaroid));
        link.addEventListener('mouseleave',  () => showPolaroid(activeService));
    });

    // Escape closes the panel
    document.addEventListener('keydown', e => {
        if (e.key === 'Escape') closePanel();
    });
}

// ==========================================================================
// FLOATING BACK-TO-TOP BUTTON
// ==========================================================================
// JS-injected so we don't have to touch every page's HTML. Hidden until
// scrolled past 400px; clicking scrolls smoothly to the top of the page.
function initBackToTop() {
    if (document.getElementById('backToTop')) return; // idempotent

    const btn = document.createElement('button');
    btn.id = 'backToTop';
    btn.className = 'back-to-top';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Scroll to top of page');

    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('class', 'back-to-top-icon');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');

    const path = document.createElementNS(svgNS, 'path');
    path.setAttribute('d', 'M12 19V5M5 12l7-7 7 7');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');

    svg.appendChild(path);
    btn.appendChild(svg);
    document.body.appendChild(btn);

    const SHOW_AFTER = 400; // px scrolled before the button appears
    let ticking = false;

    function update() {
        btn.classList.toggle('visible', window.scrollY > SHOW_AFTER);
        ticking = false;
    }

    window.addEventListener('scroll', () => {
        if (!ticking) {
            requestAnimationFrame(update);
            ticking = true;
        }
    }, { passive: true });

    btn.addEventListener('click', () => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    });

    update(); // run once in case the page loads already scrolled
}

// ==========================================================================
// HIDE FLOATING BUTTONS WHILE POLAROID HERO IS IN VIEW
// ==========================================================================
// The polaroid hero's #info-panel is centered inside a 100vh stage that GSAP
// pins to the viewport, so the user can't scroll content out from under the
// theme-toggle and back-to-top buttons in the bottom-right corner. We watch
// the FIRST section after the hero rather than the pinned hero itself: as soon
// as it enters the viewport from the bottom, the buttons are revealed. This
// fires earlier than waiting for the pinned hero to leave, and keeps the
// buttons visible for the rest of the page.
//
// Target the hero's next sibling section directly (not a hard-coded section id)
// so reordering the lower sections — e.g. moving #industries-section down past
// the CTA — never leaves the buttons hidden until the user reaches that section.
function watchPolaroidHeroForFloatingButtons() {
    const hero = document.getElementById('polaroid-hero');
    const trigger = hero && hero.nextElementSibling;
    if (!trigger || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
        ([entry]) => {
            // Hide only while the user is still ABOVE the trigger section (it's
            // below the viewport). Once it enters — or the user scrolls past it
            // entirely — buttons are visible from then on.
            const aboveTrigger = !entry.isIntersecting && entry.boundingClientRect.top > 0;
            document.body.classList.toggle('in-polaroid-hero', aboveTrigger);
        },
        { threshold: 0 }
    );
    io.observe(trigger);
}

document.addEventListener('DOMContentLoaded', () => {
    // Note: initMobileMenu() is called after template loading for pages with header components
    // initThemeToggle() is called here because the button is now in the body (not in the header template)
    initThemeToggle(); // Initialize floating theme toggle button
    initBackToTop(); // Floating scroll-to-top button (above the theme toggle)
    watchPolaroidHeroForFloatingButtons(); // Hide both floating buttons while the polaroid hero is in view
    initMouseTracking(); // Consolidated mouse tracking (DRY principle)
    initContactForm();
    initFAQAccordions();
    initScrollCircle();
    initScrollCircle2();
    initBouncingCircles();
    initHeroCardScrollAnimation();
    initStatsCountUp();
    initTextAccentUnfurl();
    initSkillsCarousel();
    initParallaxModal();
    initTiltShiftModal();
    initProductPlacementModal();
    init3DViewerModal();
    initARQRModal();
    init360ImagesModal();
    initImageToVideoModal();
    initAiAvatarsModal();
    initBrandDevelopmentModal();
    initPhotoManipulationModal();
    initAnimationModal();
    initAIChatbotsModal();
    console.log('All functions initialized including mobile menu, consolidated mouse tracking, bouncing circles, hero card scroll animation, stats count-up, text accent unfurl, parallax modal, tilt shift modal, product placement modal, 3D viewer modal, AR QR modal, 360 images modal, image to video modal, brand development modal, photo manipulation modal, and animation modal');
});

// Function to initialize theme toggle after templates are loaded
function initThemeToggleAfterTemplates() {
    // Wait a moment for templates to be fully inserted into DOM
    setTimeout(() => {
        initThemeToggle();
    }, 100);
}